import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { RoleLabel, roleNameClass, useLiveRoles } from "@/lib/liveRoles";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Volume2, VolumeX, SendHorizonal } from "lucide-react";
import VipBadge from "@/components/VipBadge";
import LifetimeBossBadge from "@/components/LifetimeBossBadge";
import TopBadge from "@/components/TopBadge";
import LiveChatGifPicker from "@/components/live-room/LiveChatGifPicker";
import { PandaGifterBadge } from "@/components/PandaGifterBadge";
import { useLiveRoomSocket } from "@/hooks/useLiveRoomWS";
import UserBadge from "@/components/UserBadge";
import { getAlertAudioContext, isBroadcasting, playTone } from "@/lib/liveAudioAlerts";
import { ChatMediaThumb, ChatMediaViewer } from "@/components/live-room/LiveChatMedia";
import { useChatAttachment, ChatAttachButton, ChatAttachmentChip } from "@/components/live-room/LiveChatAttach";
import VerifiedBadge from "@/components/VerifiedBadge";

const SOUND_PREF_KEY = "pandax_live_chat_sound_muted";
const SOUND_MIN_INTERVAL_MS = 400;

// Small "chirp" via the single shared alert AudioContext (viewers only —
// publishers are silenced in liveAudioAlerts.js to keep the live mic clean).
function playChatChirp() {
  try {
    playTone({ freq: 880, duration: 0.15, gain: 0.18 });
    playTone({ freq: 1320, duration: 0.15, gain: 0.18, delay: 0.06 });
  } catch { /* audio disabled — silent */ }
}

/**
 * Transparent chat feed that overlays the bottom-left of the live stream video,
 * Fanbase / TikTok live style. Shows the last ~8 messages with a soft fade at the top.
 * Also renders the composer input pinned to the bottom for logged-in viewers.
 *
 * Plays a subtle chirp when new messages arrive from OTHER users (not `me`),
 * rate-limited to at most one chirp per 400ms. Mute state persists in localStorage.
 */
export default function LiveChatOverlay({ streamId, me, isHost, silent = false, onSend, showComposer = true, onFocusInput, battleActive = false, duoActive = false, sideBySide = false }) {
  const [messages, setMessages] = useState([]);
  const { roles } = useLiveRoles(streamId);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [viewing, setViewing] = useState(null);
  const attach = useChatAttachment();
  const removeLocal = useCallback((id) => setMessages((prev) => prev.filter((m) => m.id !== id)), []);
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(SOUND_PREF_KEY) === "1"; } catch { return false; }
  });
  const sinceRef = useRef(null);
  const seenRef = useRef(new Set());
  const firstLoadRef = useRef(true);
  const lastPlayRef = useRef(0);
  const mutedRef = useRef(muted);
  const silentRef = useRef(silent);
  const meIdRef = useRef(me?.id);
  const scrollerRef = useRef(null);
  const atBottomRef = useRef(true);

  const isNearBottom = (el) => el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  const onScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (el) atBottomRef.current = isNearBottom(el);
  }, []);

  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { silentRef.current = silent; }, [silent]);
  useEffect(() => { meIdRef.current = me?.id; }, [me?.id]);

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      try { localStorage.setItem(SOUND_PREF_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      // First user gesture also unlocks the shared AudioContext on iOS/Safari.
      if (!next && !silentRef.current) getAlertAudioContext();
      return next;
    });
  }, []);

  // Shared dedup + apply pipeline used by BOTH the 2.5s poll AND the
  // Stage 4(c-plus) `chat:new` WS event. `seenRef` guarantees at-most-once
  // rendering regardless of which path delivered first.
  const applyIncoming = useCallback((items) => {
    if (!Array.isArray(items) || items.length === 0) return;
    const fresh = [];
    for (const m of items) {
      if (!m || !m.id) continue;
      if (seenRef.current.has(m.id)) continue;
      seenRef.current.add(m.id);
      fresh.push(m);
      if (!sinceRef.current || m.created_at > sinceRef.current) sinceRef.current = m.created_at;
    }
    if (fresh.length === 0) return;
    // Keep history for scroll-back. Trim only while pinned to the newest
    // message so a reader who scrolled up never loses their place.
    setMessages((prev) => {
      const next = [...prev, ...fresh];
      const cap = atBottomRef.current ? 200 : 600;
      return next.length > cap ? next.slice(-cap) : next;
    });
    if (!firstLoadRef.current && !mutedRef.current && !silentRef.current && !isBroadcasting()) {
      const meId = meIdRef.current;
      const hasFromOther = fresh.some((m) => m.user_id && m.user_id !== meId);
      const now = Date.now();
      if (hasFromOther && now - lastPlayRef.current >= SOUND_MIN_INTERVAL_MS) {
        lastPlayRef.current = now;
        playChatChirp();
      }
    }
    firstLoadRef.current = false;
  }, []);

  const load = useCallback(async () => {
    try {
      const params = sinceRef.current ? { since: sinceRef.current } : {};
      const { data } = await api.get(`/live/${streamId}/chat`, { params });
      applyIncoming(data);
    } catch { /* silent */ }
  }, [streamId, applyIncoming]);

  // Real-time chat on the shared room socket: `sync` (hello / reconnect)
  // carries the last 40 messages, `chat:new` streams the rest. One REST load
  // on mount paints history instantly; there is no polling loop.
  const socket = useLiveRoomSocket();
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const offs = [
      socket.subscribe("chat:new", (m) => { if (m?.message) applyIncoming([m.message]); }),
      socket.subscribe("sync", (s) => { if (Array.isArray(s?.chat)) applyIncoming(s.chat); }),
      socket.subscribe("chat:removed", (m) => { if (m?.message_id) removeLocal(m.message_id); }),
    ];
    return () => offs.forEach((off) => off());
  }, [socket, applyIncoming, removeLocal]);

  // Auto-follow only while the reader is at the newest message; when they
  // scrolled back, new messages append below without moving their view.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const attReady = attach.att?.status === "ready";
  const sendGif = async (g) => {
    if (!me || sending || !g?.url) return;
    setSending(true);
    try {
      await api.post(`/live/${streamId}/chat`, { text: "", media_url: g.url, media_type: "gif" });
      atBottomRef.current = true;
      await load();
      onSend && onSend();
    } catch { /* silent */ } finally { setSending(false); }
  };
  const send = async () => {
    if ((!text.trim() && !attReady) || sending || !me) return;
    if (attach.att && !attReady) return; // still uploading / failed — wait or retry
    setSending(true);
    try {
      const body = { text: text.trim() };
      if (attReady) { body.media_url = attach.att.url; body.media_type = attach.att.type; }
      await api.post(`/live/${streamId}/chat`, body);
      setText("");
      attach.clear();
      atBottomRef.current = true; // own message: jump back to newest
      await load();
      onSend && onSend();
    } catch { /* silent */ } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Sound / mute toggle — parked above the composer on the left so it
          never collides with the top-bar close/share/viewers cluster
          (which lives at top-3 right-3 in the parent LiveRoom).
          Slice A — hidden for the host (creator has no "speaker/mute"
          per the approved top-bar spec; this was flagged during the
          real-phone review). Viewer/cohost still see it. */}
      {!isHost && !silent && (
        <button type="button"
          onClick={toggleMute}
          aria-label={muted ? "Turn chat sound on" : "Turn chat sound off"}
          title={muted ? "Chat sound off" : "Chat sound on"}
          data-testid="live-chat-sound-toggle"
          className={`absolute z-30 h-9 w-9 grid place-items-center rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-md border border-white/15 text-white transition-colors ${sideBySide ? "left-[calc(66%+0.5rem)]" : "left-3 sm:left-4"}`}
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)" }}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      )}

      {/* Live 2.0 — chat pinned bottom-LEFT with strict max-width so faces
          (center + right half of screen) stay unobscured. Fewer visible
          messages, softer fade at top edge, no full-width backing bar.
          Respects iOS safe area so it never sits under home-indicator. */}
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        data-testid="live-chat-overlay"
        className={`pointer-events-auto absolute z-20 overflow-y-auto space-y-1.5 ${
          sideBySide
            ? "left-[calc(66%+0.5rem)] right-2 w-auto max-w-none max-h-[calc(100dvh-var(--live-header-h,3rem)-6.5rem)]" // short landscape: chat column on the right of the stage
            : battleActive || duoActive
            ? "left-2 sm:left-4 right-2 sm:right-4 w-auto max-w-none max-h-[calc(96dvh-var(--battle-arena-h)-9.5rem)]" // battle/duo: fill the freed lower region — never over video
            : "left-2 sm:left-4 w-[62vw] max-w-[380px] max-h-[32vh]"
        }`}
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)",
          // Finger scrolling stays inside the feed: no page/video movement,
          // no scroll chaining, no rubber-banding of the room shell.
          overscrollBehavior: "contain",
          touchAction: "pan-y",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 25%, rgba(0,0,0,1) 60%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 25%, rgba(0,0,0,1) 60%)",
        }}
      >
        {messages.map((m) => (
          <div key={m.id} className="flex items-start gap-2 text-sm">
            <UserBadge
              userRef={m.user_id || m.handle}
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-full shrink-0"
              data-testid={`live-chat-avatar-${m.id}`}
              aria-label={`View ${m.display_name}'s profile`}
            >
              <Avatar className={`w-6 h-6 border border-white/10 shrink-0`}>
                <AvatarImage src={m.avatar_url} />
                <AvatarFallback className="bg-rose-600 text-[10px] text-white">
                  {m.display_name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </UserBadge>
            <div className="min-w-0 flex-1 bg-black/35 backdrop-blur-md rounded-2xl px-2.5 py-1">
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <UserBadge
                  userRef={m.user_id || m.handle}
                  as="span"
                  className={`text-[11px] font-semibold truncate cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 rounded-sm ${
                    m.is_host
                      ? "text-yellow-300"
                      : roleNameClass(roles[m.user_id] ?? m.room_role, "text-white/90")
                  }`}
                  data-testid={`live-chat-handle-${m.id}`}
                >
                  {m.display_name}
                </UserBadge>
                <VerifiedBadge show={m.is_verified} size="sm" />
                {m.is_host && (
                  <span className="text-[8px] uppercase tracking-widest bg-yellow-400 text-black px-1 rounded-sm">
                    Host
                  </span>
                )}
                {!m.is_host && <RoleLabel role={roles[m.user_id] ?? m.room_role} />}
                <LifetimeBossBadge show={m.lifetime_boss} size="xs" />
                <VipBadge tier={m.vip_tier} size="xs" />
                <TopBadge show={m.top_badge} size="xs" />
                <PandaGifterBadge tier={m.gifter_badge} size="sm" />
              </div>
              {/* Message body stays inert — tapping/scrolling here must
                  behave normally, per Slice 2 spec. Only the avatar and
                  @handle above open the preview. */}
              {m.kind === "gift" && m.gift && (
                <div data-testid={`live-chat-gift-${m.id}`} className="text-[12px] text-amber-200 leading-snug flex items-center gap-1 flex-wrap">
                  <span>sent a</span><span className="text-base leading-none">{m.gift.emoji}</span><span className="font-semibold">{m.gift.label}</span>{m.gift.to_name && <span className="text-rose-300" data-testid="live-chat-gift-to">→ @{m.gift.to_name}</span>}
                  <span className="text-amber-300/80">· {Number(m.gift.coins).toLocaleString()} coins</span>
                </div>
              )}
              {m.text && <div className="text-[13px] text-white leading-snug break-words">{m.text}</div>}
              <ChatMediaThumb message={m} onOpen={setViewing} />
            </div>
          </div>
        ))}
      </div>

      {/* Composer pinned near bottom — narrower on desktop, aligned left,
          respects iPhone safe-area-inset-bottom so it never gets clipped
          by the home-indicator or Safari's bottom chrome. */}
      {showComposer && me && <ChatAttachmentChip att={attach.att} onClear={attach.clear} onRetry={attach.retry} />}
      {showComposer && me && (
        <div
          className={`absolute z-30 flex items-center gap-2 ${sideBySide ? "left-[calc(66%+0.5rem)] right-2 w-auto" : "left-2 sm:left-4 w-[70vw] max-w-[440px]"}`}
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
          data-testid="live-composer"
        >
          <ChatAttachButton fileRef={attach.fileRef} onPick={attach.pick} onOpen={attach.open} disabled={sending || attach.att?.status === "uploading"} />
          <LiveChatGifPicker onPick={sendGif} disabled={sending} />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            onFocus={onFocusInput}
            maxLength={400}
            placeholder={attach.att ? "Add a caption…" : "Say hi…"}
            className="flex-1 min-w-0 bg-black/50 backdrop-blur-md border border-white/15 rounded-full px-4 py-2.5 text-sm text-white placeholder-white/60 focus:outline-none focus:border-white/40"
            data-testid="live-chat-input"
          />
          <button type="button"
            onClick={send}
            disabled={(!text.trim() && !attReady) || sending || (attach.att && !attReady)}
            data-testid="live-chat-send"
            aria-label="Send"
            className="bg-white/15 hover:bg-white/25 disabled:opacity-40 border border-white/15 text-white text-sm rounded-full h-10 w-10 sm:w-auto sm:h-auto sm:px-4 sm:py-2.5 grid place-items-center shrink-0 backdrop-blur-md"
          >
            <SendHorizonal size={16} className="sm:hidden" />
            <span className="hidden sm:inline">Send</span>
          </button>
        </div>
      )}
      {viewing && (
        <ChatMediaViewer message={viewing} streamId={streamId} me={me} isHost={isHost} onClose={() => setViewing(null)} onRemoved={removeLocal} />
      )}
    </>
  );
}
